/**
 * Opening hours, one row per weekday.
 *
 * Closing after midnight is the case this editor exists for. A Friday that runs
 * to 02:30 is entered exactly as it is written on the door — open 11:00, close
 * 02:30 — and the row says "next day" back, along with how long that actually
 * leaves the screens on, so nobody has to work out whether they just asked for
 * fifteen and a half hours or minus eight and a half.
 *
 * Several periods per day are allowed because the manifest allows them: the
 * resolver treats the entries as a union, so a lunch service and a dinner
 * service on the same day are two rows rather than a lie about the afternoon.
 */

import type { ReactElement } from 'react';
import type { IsoDayOfWeekValue } from '@pbtv/shared';
import {
  DAY_NAMES,
  DEFAULT_PERIOD,
  describePeriod,
  isAlwaysOpen,
} from '../../lib/openingHours.js';
import type { DayHours, HoursIssue, HoursPeriod } from '../../lib/openingHours.js';

export interface OpeningHoursEditorProps {
  value: readonly DayHours[];
  onChange: (next: DayHours[]) => void;
  issues: readonly HoursIssue[];
  disabled?: boolean;
}

function replaceDay(
  week: readonly DayHours[],
  day: IsoDayOfWeekValue,
  periods: HoursPeriod[],
): DayHours[] {
  return week.map((row) => (row.day === day ? { day: row.day, periods } : row));
}

export function OpeningHoursEditor({
  value,
  onChange,
  issues,
  disabled = false,
}: OpeningHoursEditorProps): ReactElement {
  const setPeriod = (
    day: IsoDayOfWeekValue,
    index: number,
    field: keyof HoursPeriod,
    next: string,
  ): void => {
    const row = value.find((candidate) => candidate.day === day);
    if (row === undefined) return;
    const periods = row.periods.map((period, position) =>
      position === index ? { ...period, [field]: next } : period,
    );
    onChange(replaceDay(value, day, periods));
  };

  const addPeriod = (day: IsoDayOfWeekValue): void => {
    const row = value.find((candidate) => candidate.day === day);
    if (row === undefined) return;
    const last = row.periods[row.periods.length - 1];
    const seed: HoursPeriod = last === undefined ? { ...DEFAULT_PERIOD } : { ...last };
    onChange(replaceDay(value, day, [...row.periods, seed]));
  };

  const removePeriod = (day: IsoDayOfWeekValue, index: number): void => {
    const row = value.find((candidate) => candidate.day === day);
    if (row === undefined) return;
    onChange(
      replaceDay(
        value,
        day,
        row.periods.filter((_, position) => position !== index),
      ),
    );
  };

  const copyToEveryDay = (day: IsoDayOfWeekValue): void => {
    const row = value.find((candidate) => candidate.day === day);
    if (row === undefined) return;
    onChange(
      value.map((candidate) => ({
        day: candidate.day,
        periods: row.periods.map((period) => ({ ...period })),
      })),
    );
  };

  return (
    <fieldset className="hours" disabled={disabled}>
      <legend className="field__label">Opening hours</legend>

      <div className="hours__rows">
        {value.map((row) => {
          const dayIssues = issues.filter((issue) => issue.day === row.day);

          return (
            <div className="hours-row" key={row.day}>
              <div className="hours-row__day">
                <span className="hours-row__name">{DAY_NAMES[row.day]}</span>
                {row.periods.length > 0 ? (
                  <button
                    type="button"
                    className="button button--link"
                    onClick={() => copyToEveryDay(row.day)}
                  >
                    Copy to every day
                  </button>
                ) : null}
              </div>

              <div className="hours-row__periods">
                {row.periods.length === 0 ? (
                  <p className="hours-row__closed">Closed all day</p>
                ) : null}

                {row.periods.map((period, index) => (
                  <div className="hours-period" key={index}>
                    <label className="hours-period__time">
                      <span className="visually-hidden">{DAY_NAMES[row.day]} opening time</span>
                      <input
                        className="input input--time"
                        type="time"
                        required
                        value={period.open}
                        onChange={(event) =>
                          setPeriod(row.day, index, 'open', event.currentTarget.value)
                        }
                      />
                    </label>

                    <span className="hours-period__to" aria-hidden="true">
                      to
                    </span>

                    <label className="hours-period__time">
                      <span className="visually-hidden">{DAY_NAMES[row.day]} closing time</span>
                      <input
                        className="input input--time"
                        type="time"
                        required
                        value={period.close}
                        onChange={(event) =>
                          setPeriod(row.day, index, 'close', event.currentTarget.value)
                        }
                      />
                    </label>

                    <span className="hours-period__reading">{describePeriod(period)}</span>

                    <button
                      type="button"
                      className="button button--ghost button--small"
                      onClick={() => removePeriod(row.day, index)}
                    >
                      Remove
                      <span className="visually-hidden">
                        {' '}
                        the {DAY_NAMES[row.day]} period starting {period.open}
                      </span>
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="button button--ghost button--small"
                  onClick={() => addPeriod(row.day)}
                >
                  {row.periods.length === 0 ? 'Set hours' : 'Add another period'}
                </button>

                {dayIssues.map((issue) => (
                  <p className="hours-row__issue" key={issue.message}>
                    {issue.message}
                  </p>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {isAlwaysOpen(value) ? (
        <p className="alert alert--warning" role="status">
          With no hours set at all, these screens never show the closed state — they play around
          the clock. Set hours on at least one day if that is not what you want.
        </p>
      ) : null}
    </fieldset>
  );
}
